import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Mail, Loader2, CheckCircle } from 'lucide-react';
import { requestMagicLink } from '../services/api';

const MagicLinkRequest = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await requestMagicLink(email);
      setIsSent(true);
    } catch (err) {
      console.error('Magic link request error:', err);
      setError(err.message || 'Failed to send magic link');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern">
      {/* Header */}
      <header className="border-b border-calm-border bg-fern-card/80 backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-6 py-5 flex items-center justify-between">
          <div 
            className="text-2xl font-bold text-calm-text cursor-pointer" 
            style={{fontFamily: 'DM Serif Display, serif'}}
            onClick={() => navigate('/')}
          >
            OHMNAI
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-calm-text-secondary hover:text-forest hover:bg-fern"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-md mx-auto px-6 py-20">
        <div className="bg-fern-card rounded-2xl p-10 border border-calm-border shadow-lg">
          {!isSent ? (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-herbal-light rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-8 w-8 text-forest" />
                </div>
                <h1 className="text-2xl text-calm-text mb-2" style={{fontFamily: 'DM Serif Display, serif'}}>
                  Sign in with email
                </h1>
                <p className="text-calm-text-secondary">
                  Enter your email and we&apos;ll send you a magic link to sign in.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full py-6 px-4 text-base bg-fern-light border-calm-border focus:border-forest focus:ring-forest/20"
                    disabled={isLoading}
                  />
                </div>

                {error && (
                  <div className="bg-red-50 rounded-xl p-4 border border-red-200">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={isLoading || !email}
                  className="w-full bg-forest hover:bg-forest-dark text-white py-6 text-base rounded-xl transition-colors"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending link...
                    </>
                  ) : (
                    'Send magic link'
                  )}
                </Button>
              </form>

              <p className="text-sm text-calm-text-secondary text-center mt-6">
                No password needed. We&apos;ll email you a secure sign-in link.
              </p>
            </>
          ) : (
            <div className="text-center space-y-6 fade-in-up">
              <div className="w-20 h-20 bg-herbal-light rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="h-12 w-12 text-forest" />
              </div>
              <h1 className="text-2xl text-calm-text" style={{fontFamily: 'DM Serif Display, serif'}}>
                Check your email
              </h1>
              <p className="text-calm-text-secondary">
                We&apos;ve sent a magic link to <strong className="text-forest">{email}</strong>
              </p>
              <p className="text-sm text-calm-text-secondary">
                Click the link in your email to sign in. The link expires in 15 minutes.
              </p>
              <Button
                onClick={() => {
                  setIsSent(false);
                  setEmail('');
                }}
                variant="outline"
                className="border-calm-border text-forest hover:bg-fern px-8 py-3 text-base rounded-xl"
              >
                Use a different email
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MagicLinkRequest;
